import { Button, Progress } from '@invent/wl-ui-kit-next/ui';
import { Lock } from 'lucide-react';
import * as React from 'react';

import {
  CompactBar,
  CompactTitle,
  DataTable,
  NotPermitted,
  ReasonDialog,
  STICKY_TABLE_TOP,
  StatusChip,
  useScrolledPast,
  type IDataColumn
} from '@/design';
import { useCan } from '@/domains/auth';
import { ApiError, ProblemType, SAVE_REFUSED, saveFile } from '@/domains/api';
import { formatDateTime } from '@/domains/survey';
import type { IReportRun } from '../../api/lists-contracts';
import { listsApi } from '../../api/lists-api';
import { LoadError, useAdminData } from '../../components/admin-page';
import { runStateChip } from '../../lists';
import { refusalMessage } from '../../refusal';
import { WorkPageHeaderActions, useReadAt } from '../lists/work-page/header-actions';
import { MAX_SHOWN, PER_PAGE, WorkPager, usePerPage } from '../lists/work-page/pager';
import { useReport } from './report';

/**
 * A report's runs (SR-230, design `docs/superpowers/specs/2026-10-08-lists-reports-import-ui.md`
 * §4.2, survey-api's §9 and D6) — **Run now** keeps the query and the file, which the Worker writes;
 * *Export now…* is a file at once and keeps nothing (LST-07).
 *
 * **A run is asked for, then written** (`POST /views/{id}/runs` answers 202 `queued`): it is drawn
 * first at once, *Being written*, and every run still `queued` is read again every 5 s on a visible
 * tab (`GET /report-runs/{id}`) until survey-api says *Ready* or *Failed* — every 2 s while one is
 * being written (SR-418), so its bar moves — never over a read still out. Nothing else polls. On
 * `local` no Worker runs, so a run stays *Being written*, *Waiting to start* (§11.1).
 *
 * **A run being written says how far it is** from the Worker's `rowsTotal` and `rowsWritten`: a
 * determinate bar and "12,000 of 37,278 rows", *Saving the file…* once every row is written,
 * *Reading the rows…* while the Worker orders them, and *Waiting to start* before it has taken it.
 *
 * **The tab is a light work page inside the record** (SR-515, the work pages design, "Three forms"):
 * the tab's *Refresh* is handed to the app bar (`WorkPageHeaderActions`, drawn in the tab's own
 * line beside the key number — the record's header is not a work page's row), a compact bar once
 * the line scrolls away, the table under a sticky header, and the work pages' pager. survey-api
 * answers a report's runs whole, so **the pager pages the list already read** — paging, not
 * filtering; nothing is filtered or counted here. No funnels: a filter would be the browser's.
 *
 * **The file comes through the client** (`GET …/file`, `apiDownload`), never a link: a link would
 * carry no bearer. **A restricted run's file is `internal_fields.restricted`'s** — the run says so
 * with a lock, and its *Download* is disabled with `NotPermitted`'s sentence for anyone else; a 403
 * that still comes (roles changed under the page) is said beside the button. A failed run's `error`
 * is drawn as sent.
 */

/** survey-api's re-read of a written run, on a visible tab (§4.2). */
const RUN_POLL_MS = 5_000;

/** The re-read while the Worker is writing a run — it moves `rowsWritten` about once a second. */
const RUN_WRITING_POLL_MS = 2_000;

/** survey-api's bound on a run's note — said beside the box, never enforced here. */
const NOTE_MAX = 2000;

/** What the runs' screen said: a press's answer, or the poll's own note (`poll`), which the next
 *  tick that reads takes back — and only that. */
export interface IRunsSaid {
  text: string;
  alert: boolean;
  poll?: boolean;
}

export interface IReportRuns {
  /** The runs, newest first; null while the first read is out or after it failed. */
  runs: Nullable<IReportRun[]>;
  /** The report is gone — the runs' read answered 404. */
  gone: boolean;
  error: Nullable<string>;
  /** The runs' read is out — the first, or a *Refresh*. */
  reading: boolean;
  /** What the last ask or poll said, over the table. */
  said: Nullable<IRunsSaid>;
  reload: () => Promise<boolean>;
  /** A run just asked for, drawn first. */
  add: (run: IReportRun) => void;
  say: (said: Nullable<IRunsSaid>) => void;
}

/** The runs of a saved report — none for a report not saved yet — and the 5 s poll of the queued.
 *  A queued run the poll finds gone (404) sends the runs' list to be read again, so it leaves the
 *  queue rather than being asked about every 5 s. */
export function useReportRuns(viewId: Nullable<string>): IReportRuns {
  const read = useAdminData(
    async (signal) => (viewId === null ? { runs: [] } : { runs: await listsApi.runs(viewId, signal) }),
    [viewId]
  );
  const [said, setSaid] = React.useState<Nullable<IRunsSaid>>(null);
  const runs = read.data?.runs ?? null;
  const current = React.useRef(runs);
  current.current = runs;
  const { adopt, reload } = read;

  const add = React.useCallback(
    (run: IReportRun) => {
      adopt({ runs: [run, ...(current.current ?? []).filter((one) => one.id !== run.id)] });
    },
    [adopt]
  );

  const queued = (runs ?? []).filter((run) => run.state === 'queued');
  const queuedKey = queued.map((run) => run.id).join(',');
  const pollMs = queued.some((run) => (run.rowsWritten ?? null) !== null)
    ? RUN_WRITING_POLL_MS
    : RUN_POLL_MS;
  // Outlives the interval, which a change of pace restarts: a read still out holds the next tick.
  const out = React.useRef(false);
  React.useEffect(() => {
    if (queuedKey === '') return;
    const ids = queuedKey.split(',');
    const timer = window.setInterval(() => {
      if (out.current || document.visibilityState !== 'visible') return;
      out.current = true;
      void Promise.all(ids.map((id) => listsApi.run(id)))
        .then((fresh) => {
          const byId = new Map(fresh.flatMap((run) => (run ? [[run.id, run] as const] : [])));
          const held = current.current;
          if (held) adopt({ runs: held.map((run) => byId.get(run.id) ?? run) });
          setSaid((now) => (now?.poll ? null : now));
          if (fresh.some((run) => run === null)) void reload();
        })
        .catch((error) =>
          setSaid({
            text: `Could not read the runs being written — showing them as last read. ${refusalMessage(error, 'No answer')}`,
            alert: false,
            poll: true
          })
        )
        .finally(() => {
          out.current = false;
        });
    }, pollMs);
    return () => window.clearInterval(timer);
  }, [queuedKey, pollMs, adopt, reload]);

  return {
    runs,
    gone: read.data !== null && read.data.runs === null,
    error: read.error,
    reading: read.reading,
    said,
    reload: read.reload,
    add,
    say: setSaid
  };
}

/**
 * *Run now…* — the note (optional, ≤ 2,000 said), then `POST /views/{id}/runs`. The run is of the
 * report **as saved**: survey-api binds the stored view, so a changed screen is said in the dialog.
 * A 400 (a field this caller may no longer see) is said in the dialog in survey-api's words; a 404,
 * a 409 `not-a-report` and an answer that never arrived close it and are said over the runs.
 */
export function RunNowDialog({
  open,
  onOpenChange,
  viewId,
  changed,
  runs,
  onAsked,
  sayProblem
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  viewId: string;
  /** The screen differs from the report as saved. */
  changed: boolean;
  runs: IReportRuns;
  /** The run landed or the answer is to be read under Runs — the record shows the tab. */
  onAsked: () => void;
  sayProblem: (problem: { target: string; reason: string }) => string;
}) {
  return (
    <ReasonDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Run the report"
      description={
        <>
          Every row this report matches is written to a file, kept under Runs with its query. It is
          drawn <em>Being written</em> until it is ready to download.
          {changed && ' The run is of the report as saved — your unsaved changes are not in it.'}
        </>
      }
      reasonLabel="Note"
      required={false}
      max={NOTE_MAX}
      confirmLabel="Run now"
      onConfirm={async (note) => {
        try {
          const run = await listsApi.requestRun(viewId, note.trim() === '' ? undefined : note);
          runs.add(run);
          runs.say({ text: 'The run was asked for — it is being written.', alert: false });
          onAsked();
          return null;
        } catch (error) {
          if (error instanceof ApiError && error.status === 400) {
            const problems = error.problem?.problems ?? [];
            return problems.length > 0
              ? `The report cannot be run: ${problems.map(sayProblem).join('; ')}.`
              : refusalMessage(error, 'The report cannot be run.');
          }
          const text =
            error instanceof ApiError && error.status === 404
              ? 'This report is no longer there — deleted since it was opened.'
              : error instanceof ApiError && error.problem?.type === ProblemType.notAReport
                ? 'This view is a list, not a report — open it on Lists.'
                : error instanceof ApiError && error.status < 500
                  ? refusalMessage(error, 'The report could not be run.')
                  : 'The answer did not arrive — the runs are read again; see whether the run was asked for before running it again.';
          runs.say({ text, alert: true });
          void runs.reload();
          onAsked();
          return null;
        }
      }}
    />
  );
}

/** The Runs tab: the runs newest first, a page of them at a time, each one's file. */
export function ReportRunsTab() {
  const report = useReport();
  const { runs } = report;
  const can = useCan();
  const mayRestricted = can('internal_fields.restricted');
  const restrictedReason = React.useId();
  // The tab's window is its own state, not the address's: the address and its state are the
  // reports list's beside the record, and a write of `page` here would lose them.
  const [perPage, choosePerPage] = usePerPage();
  const [shownPages, setShownPages] = React.useState({ page: 1, pages: 1 });
  const readAt = useReadAt(runs.runs);
  const [controlsEnd, setControlsEnd] = React.useState<Nullable<HTMLElement>>(null);
  const scrolled = useScrolledPast(controlsEnd);

  if (report.saved === null) {
    return <p className="text-sm text-muted-foreground">Save the report to run it — a run is of a saved report.</p>;
  }
  const name = report.saved.body.name;
  const columns: IDataColumn<IReportRun>[] = [
    { id: 'when', header: 'When', className: 'whitespace-nowrap', cell: (run) => formatDateTime(run.requestedAt) },
    { id: 'who', header: 'Who', cell: (run) => run.requestedBy.email },
    {
      id: 'note',
      header: 'Note',
      cell: (run) => run.note ?? <span className="text-muted-foreground">None</span>
    },
    {
      id: 'state',
      header: 'State',
      cell: (run) => {
        const chip = runStateChip(run.state);
        return (
          <span data-run-state={run.state} className="inline-grid justify-items-start gap-1">
            <StatusChip tone={chip.tone}>{run.state === 'queued' ? `${chip.label}…` : chip.label}</StatusChip>
            {run.state === 'queued' && <RunProgress run={run} />}
          </span>
        );
      }
    },
    {
      id: 'rows',
      header: 'Rows',
      numeric: true,
      cell: (run) => (run.rowCount === null ? '—' : run.rowCount.toLocaleString('en-US'))
    },
    {
      id: 'file',
      header: 'File',
      cell: (run) => (
        <RunFile
          run={run}
          reportName={name}
          locked={run.restricted && !mayRestricted ? restrictedReason : null}
        />
      )
    },
    {
      id: 'error',
      header: 'Why it failed',
      cell: (run) => run.error ?? ''
    }
  ];

  const all = runs.runs ?? [];
  // The window of the list as read: survey-api sends every run, the pager draws a page of them.
  const from = (shownPages.page - 1) * perPage;
  const visible = all.slice(from, from + shownPages.pages * perPage);
  const refresh = { reading: runs.reading, readAt, onClick: () => void runs.reload() };
  const total = runs.runs?.length ?? null;
  const summary = total === null ? null : `${total.toLocaleString('en-US')} ${total === 1 ? 'run' : 'runs'}`;

  return (
    <div className="grid gap-3">
      <CompactBar
        shown={scrolled}
        label={`Runs of ${name}, compact`}
        actions={<WorkPageHeaderActions summary={null} refresh={refresh} help={null} />}
      >
        <CompactTitle>{name}</CompactTitle>
        <span className="shrink-0 text-[0.8125rem] text-muted-foreground">Runs</span>
      </CompactBar>
      <div className="flex flex-wrap items-center gap-3">
        {runs.said && (
          <p
            role={runs.said.alert ? 'alert' : 'status'}
            className={`text-sm ${runs.said.alert ? 'text-destructive' : 'text-muted-foreground'}`}
          >
            {runs.said.text}
          </p>
        )}
        <span className="ml-auto flex items-center gap-2">
          <WorkPageHeaderActions
            summary={summary}
            summaryData={{ 'data-runs-summary': '' }}
            refresh={refresh}
            help={null}
          />
        </span>
      </div>
      <div ref={setControlsEnd} aria-hidden data-controls-end="" className="-mt-3 h-0" />
      {runs.error && runs.runs !== null && (
        <LoadError message={`Could not refresh — showing the runs as last read. ${runs.error}`} />
      )}
      {!mayRestricted && (runs.runs ?? []).some((run) => run.restricted) && (
        <NotPermitted id={restrictedReason} what="reading restricted fields — a run marked with a lock holds one" />
      )}
      <DataTable
        label={`Runs of ${name}`}
        columns={columns}
        rows={visible.length === 0 ? null : visible}
        rowKey={(run) => run.id}
        rowProps={(run) => ({ 'data-run': run.id }) as React.HTMLAttributes<HTMLTableRowElement>}
        state={
          runs.runs === null
            ? runs.gone
              ? { kind: 'empty', content: 'This report is no longer there — deleted since it was opened.' }
              : runs.error
                ? { kind: 'error', content: <LoadError message={`The runs could not be read. ${runs.error}`} /> }
                : { kind: 'empty', content: 'Reading the runs…' }
            : all.length > 0
              ? { kind: 'empty', content: 'This page is past the end of the runs.' }
              : { kind: 'empty', content: 'Not run yet — Run now writes its file and keeps it here.' }
        }
        stickyTop={STICKY_TABLE_TOP}
      />
      {runs.runs !== null && (
        <WorkPager
          page={shownPages.page}
          perPage={perPage}
          perPages={PER_PAGE}
          pages={shownPages.pages}
          maxShown={MAX_SHOWN}
          total={all.length}
          onPage={(page) => setShownPages({ page, pages: 1 })}
          onPerPage={(size) => {
            choosePerPage(size);
            setShownPages({ page: 1, pages: 1 });
          }}
          onMore={() => setShownPages((now) => ({ ...now, pages: now.pages + 1 }))}
          sticky
        />
      )}
    </div>
  );
}

/** How far the Worker is with a queued run: waiting, rows written of the total, or saving. */
function RunProgress({ run }: { run: IReportRun }) {
  const total = run.rowsTotal ?? null;
  const written = run.rowsWritten ?? null;
  if (total === null && written === null) {
    return (
      <span data-run-progress="waiting" className="text-xs text-muted-foreground">
        Waiting to start
      </span>
    );
  }
  if (total === null) {
    return (
      <span data-run-progress="reading" className="text-xs text-muted-foreground">
        Reading the rows…
      </span>
    );
  }
  const done = Math.min(written ?? 0, total);
  const saving = done === total;
  return (
    <span data-run-progress={saving ? 'saving' : 'writing'} className="grid w-44 gap-1">
      <Progress
        role="progressbar"
        aria-label="Rows written"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={done}
        value={total === 0 ? 100 : (done / total) * 100}
        className="h-1.5"
      />
      <span className="text-xs text-muted-foreground tabular-nums">
        {saving
          ? 'Saving the file…'
          : `${done.toLocaleString('en-US')} of ${total.toLocaleString('en-US')} rows`}
      </span>
    </span>
  );
}

/** A run's file: *Download* once it is ready, the lock on a restricted one, what a refusal said. */
function RunFile({
  run,
  reportName,
  locked
}: {
  run: IReportRun;
  reportName: string;
  /** `NotPermitted`'s id where this reader may not read the run's restricted file. */
  locked: Nullable<string>;
}) {
  const [busy, setBusy] = React.useState(false);
  const [said, setSaid] = React.useState<Nullable<string>>(null);
  if (run.state !== 'done') return null;

  const download = async () => {
    setBusy(true);
    setSaid(null);
    try {
      const { file, fileName } = await listsApi.runFile(run.id);
      if (!saveFile(file, fileName ?? `${reportName} ${run.requestedAt.slice(0, 10)}.xlsx`)) {
        setSaid(SAVE_REFUSED);
      }
    } catch (error) {
      setSaid(
        error instanceof ApiError && error.status === 403
          ? 'Your Team Portal roles do not include reading restricted fields — this file holds one.'
          : error instanceof ApiError && error.status === 404
            ? 'This run is no longer there.'
            : refusalMessage(error, 'The file could not be read.')
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <span className="inline-flex flex-wrap items-center gap-2">
      {run.restricted && (
        <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
          <Lock className="size-3.5" aria-hidden />
          For readers of restricted fields
        </span>
      )}
      <Button
        type="button"
        variant="outline"
        className="h-7 px-2.5"
        disabled={busy || locked !== null}
        aria-describedby={locked ?? undefined}
        aria-label={`Download the run of ${formatDateTime(run.requestedAt)}`}
        onClick={() => void download()}
      >
        {busy ? 'Downloading…' : 'Download'}
      </Button>
      {said && (
        <span role="alert" className="text-xs text-destructive">
          {said}
        </span>
      )}
    </span>
  );
}
